export const en = {
  common: {
    appName: "Zeeta",
    continue: "Continue",
    back: "Back",
    save: "Save",
    cancel: "Cancel",
    signOut: "Sign out",
    loading: "Loading…",
    retry: "Try again",
    optional: "Optional",
    language: "Language",
    account: "Account",
    switchBusiness: "Switch business",
    addBusiness: "Add a business",
  },
  errors: {
    generic: "Something went wrong. Please try again.",
    network_error: "No internet connection. Check your connection and try again.",
    otp_invalid: "That code is incorrect.",
    otp_expired: "That code has expired. Request a new one.",
    otp_attempts_exceeded: "Too many wrong attempts. Request a new code.",
    captcha_failed: "Verification failed. Please try again.",
    rate_limited: "Too many attempts. Please wait a moment and try again.",
    service_unavailable: "The service is busy. Please try again shortly.",
    validation_failed: "Please check the highlighted fields.",
    conflict: "This could not be completed.",
    tenant_not_ready: "This business is still being set up.",
    forbidden: "You don't have permission to do this.",
    not_found: "Not found.",
    plan_limit_reached: "Your plan does not allow this. Upgrade from Plan & billing.",
    step_up_required: "Your admin session closed. Confirm it is you again.",
    maintenance: "Zeeta is read-only for maintenance right now. Try again soon.",
  },
  auth: {
    forgotLink: "Forgot password?",
    backToSignIn: "Back to sign in",
    resetTitle: "Reset your password",
    resetSubtitle: "We'll email a 6-digit code to your Gmail. Then choose a new password.",
    resetSend: "Email me a code",
    resetSent:
      "If {email} has a Zeeta account, we've emailed it a 6-digit code. Enter it with your new password.",
    resetSentToMe: "We emailed a 6-digit code to {email}. Enter it with your new password.",
    resetSave: "Set new password",
    title: "Sign in to Zeeta",
    subtitle: "New here? Signing in creates your account.",
    google: "Continue with Google",
    or: "or",
    tabCode: "Email code",
    tabPassword: "Password",
    emailLabel: "Gmail address",
    emailPlaceholder: "you@gmail.com",
    emailInvalid: "Use a Gmail address (ending in @gmail.com)",
    passwordLabel: "Password",
    signIn: "Sign in",
    sendCode: "Send code",
    codeSentToEmail: "If {email} is your Gmail, we sent a 6-digit code there. Check Spam too.",
    codeSentTo: "Enter the code sent to {phone}",
    codeLabel: "6-digit code",
    verify: "Verify and continue",
    trustDevice: "Keep me signed in on this device for 30 days",
    resendIn: "Resend code in {seconds}s",
    resend: "Resend code",
    changeEmail: "Use a different email",
    changeNumber: "Change number",
    emailLink: "Sign in with Gmail instead",
    phoneTitle: "Sign in with your mobile number",
    phoneSubtitle: "For accounts created with a phone number. You'll be asked to add your Gmail.",
    phoneLabel: "Mobile number",
    phonePlaceholder: "01XXXXXXXXX",
    phoneInvalid: "Enter a valid Bangladeshi mobile number",
    errors: {
      google_unavailable: "Google sign-in is not available right now. Use an email code.",
      google_cancelled: "Google sign-in was cancelled.",
      google_expired: "The Google sign-in took too long. Please try again.",
      google_not_gmail:
        "Use a personal Gmail account. Work or school Google accounts are not accepted.",
      google_conflict: "This Gmail is already linked to a different Google account.",
      google_failed: "Google sign-in did not work. Please try again or use an email code.",
    },
  },
  onboarding: {
    title: "Set up your business",
    stepOf: "Step {step} of {total}",
    profileTitle: "What's your name?",
    profileSubtitle: "Your staff and customers will see this on receipts and messages.",
    yourName: "Your full name",
    languageTitle: "Choose your language",
    languageSubtitle: "You can change this any time.",
    typeTitle: "What kind of business do you run?",
    typeSubtitle: "We'll set up units, categories and fields to match.",
    comingSoon: "Coming soon",
    detailsTitle: "Business details",
    detailsSubtitle: "This appears on your receipts and invoices.",
    shopName: "Business name",
    shopPhone: "Business phone",
    division: "Division",
    district: "District",
    upazila: "Upazila / thana",
    addressLine: "Address",
    addressPlaceholder: "House, road, area",
    selectPlaceholder: "Select",
    bin: "VAT BIN",
    binHelp:
      "Your NBR BIN. Mushak 6.3 VAT invoices can be turned on from Settings on the Starter plan.",
    fieldInvalid: "Please check this field",
    create: "Create business",
    creating: "Setting up your business…",
  },
  dashboard: {
    welcome: "Welcome, {name}",
    welcomeNoName: "Welcome",
    setupTitle: "Finish setting up",
    setupProgress: "{done} of {total} done",
    dismiss: "Hide checklist",
    markDone: "Mark done",
    done: "Done",
    branches: "Branches",
    main: "Main",
    role: "Your role",
    salesToday: "Sales today",
    salesCount: "{count} sales",
    grossProfit: "Gross profit",
    cashInHand: "Cash in drawer",
    receivable: "Customers owe you",
    payable: "You owe suppliers",
    lowStock: "Low stock items",
    nearExpiry: "Batches expiring in 90 days",
    expired: "{count} expired batches",
    trend: "Sales, last 30 days",
    newSale: "New sale",
    newPurchase: "New purchase",
    addProduct: "Add product",
    steps: {
      products: "Add your products",
      opening_stock: "Enter opening stock",
      opening_dues: "Enter opening customer and supplier dues",
      printer: "Connect a receipt printer",
      staff: "Invite your staff",
      payment_methods: "Set up cash, bKash and Nagad accounts",
      first_sale: "Make your first sale",
    },
  },
  ui: {
    item: "Item",
    choose: "Choose…",
    copy: "Copy",
    copied: "Copied",
    search: "Search",
    add: "Add",
    edit: "Edit",
    create: "Create",
    close: "Close",
    confirm: "Confirm",
    all: "All",
    total: "Total",
    date: "Date",
    status: "Status",
    noResults: "Nothing found",
    loadMore: "Load more",
    print: "Print",
    from: "From",
    to: "To",
    amount: "Amount",
    note: "Note",
    reason: "Reason",
    branch: "Branch",
    qty: "Qty",
    unit: "Unit",
    price: "Price",
    cost: "Cost",
    name: "Name",
    phone: "Phone",
    address: "Address",
    view: "View",
    remove: "Remove",
    saved: "Saved",
    active: "Active",
    archived: "Archived",
    archive: "Archive",
    restore: "Restore",
    today: "Today",
    last7: "Last 7 days",
    last30: "Last 30 days",
  },
  nav: {
    couriers: "Couriers",
    storefront: "Online shop",
    orders: "Online orders",
    developers: "Developers",
    dashboard: "Dashboard",
    pos: "Point of sale",
    sales: "Sales",
    products: "Products",
    stock: "Stock",
    purchases: "Purchases",
    customers: "Customers",
    suppliers: "Suppliers",
    wholesale: "Wholesale",
    accounts: "Cash & accounts",
    expenses: "Expenses",
    reports: "Reports",
    vat: "VAT",
    settings: "Settings",
    team: "Team",
    billing: "Plan & billing",
    loyalty: "Loyalty & vouchers",
    quotations: "Quotations",
    sessions: "Account and security",
    more: "More",
  },
  products: {
    scanHint:
      "Scan the barcode on the pack (or type it and press Enter): Zeeta fills in what it knows. Several? Separate with commas.",
    lookingUp: "Looking up the barcode…",
    alreadyHave: "This barcode is already on “{name}”.",
    openIt: "Open it",
    checkFilled: "Check the details, add your price, and save.",
    notFoundBarcode: "Not found anywhere yet: type the details. Once saved, it scans at the till.",
    stockNow: "Stock in {branch}",
    stockQty: "Quantity in hand now",
    costPrice: "Buying price (each)",
    costHint: "For stock value and profit.",
    costNeeded: "Enter what each one cost",
    moreDetails: "More details: MRP, brand, reorder level, VAT, pack sizes, photos…",
    saveNext: "Save & add next",
    savedNext: "Saved “{name}”. Scan the next product.",
    stockPerVariant: "This product has sizes or colours: set their stock on the Stock page.",
    counted: "Counted now",
    correctionWhy: "Why",
    correctionReason: "Stock count correction",
    correct: "Correct stock",
    filledFrom: {
      library: "Filled in from Zeeta's barcode library.",
      starter: "Filled in from Zeeta's starter catalogue.",
      openfoodfacts: "Filled in from",
    },
    online: "Online shop",
    photos: "Photos",
    descriptionBn: "Description (Bangla)",
    descriptionEn: "Description (English)",
    onlineHidden: "Hide from the online shop",
    onlineHiddenHint: "Still sold at the counter; not shown on your website or the API catalog.",
    title: "Products",
    subtitle: "Everything you sell, with prices and stock.",
    scanAnywhere:
      "Scan a product's barcode on this page to add it, or to open it if you already have it.",
    add: "Add product",
    editTitle: "Edit product",
    nameEn: "Name (English)",
    nameBn: "Name (Bangla)",
    nameHint: "Fill at least one. Both are searchable.",
    category: "Category",
    noCategory: "No category",
    newCategory: "New category",
    brand: "Brand",
    baseUnit: "Selling unit",
    salePrice: "Sale price",
    mrp: "MRP",
    vatRate: "VAT %",
    sdRate: "SD %",
    reorderLevel: "Low-stock alert at",
    trackStock: "Track stock for this product",
    sku: "SKU",
    skuAuto: "Leave empty to generate",
    barcodes: "Barcodes",
    barcodesHint: "Scan or type; separate several with commas.",
    packSizes: "Pack sizes",
    packSizesHint: "Buy or sell in boxes or strips, e.g. 1 box = 10 strips.",
    addPackSize: "Add pack size",
    unitsPer: "{unit} in one",
    packPrice: "Pack price",
    onHand: "In stock",
    lowStock: "Low stock",
    lowStockOnly: "Low stock only",
    empty: "No products yet",
    emptyHint: "Add your first product to start selling.",
    customFields: "More details",
    variants: "Variants",
  },
  stock: {
    title: "Stock",
    subtitle: "Stock levels, batches and corrections.",
    openingStock: "Opening stock",
    openingStockHint: "Enter what you already have on the shelf, at what it cost you.",
    adjust: "Adjust stock",
    adjustTitle: "Stock correction",
    type: "Type",
    types: {
      adjustment: "Correction",
      damage: "Damaged",
      expiry_writeoff: "Expired",
    },
    qtyChange: "Change (+ or −)",
    qtyRemove: "Quantity to remove",
    unitCost: "Cost per unit",
    batchNo: "Batch",
    expiryDate: "Expiry date",
    mfgDate: "Manufacture date",
    lots: "Batches",
    nearExpiry: "Expiring soon",
    expired: "Expired",
    expires: "Expires",
    received: "Received",
    movements: "Stock history",
    addLine: "Add product",
    posted: "Stock updated",
    value: "Stock value",
    movementTypes: {
      opening: "Opening",
      purchase: "Purchase",
      purchase_return: "Return to supplier",
      sale: "Sale",
      sale_return: "Customer return",
      transfer_in: "Transfer in",
      transfer_out: "Transfer out",
      adjustment: "Correction",
      damage: "Damaged",
      expiry_writeoff: "Expired",
      count: "Stock count",
      deficit_settle: "Cost correction",
    },
  },
  purchases: {
    title: "Purchases",
    subtitle: "Goods received from suppliers.",
    new: "New purchase",
    supplier: "Supplier",
    selectSupplier: "Choose a supplier",
    supplierInvoiceNo: "Supplier invoice no.",
    supplierInvoiceDate: "Invoice date",
    lines: "Products",
    searchItem: "Search or scan a product",
    unitCost: "Cost",
    discount: "Discount",
    vat: "VAT %",
    billDiscount: "Bill discount",
    landedCost: "Transport & other charges",
    landedHint: "Added to product cost",
    allocation: "Spread charges by",
    byValue: "Value",
    byQty: "Quantity",
    payNow: "Pay now",
    payFrom: "Pay from",
    paid: "Paid",
    due: "Due",
    save: "Save purchase",
    number: "GRN no.",
    detail: "Purchase",
    return: "Return to supplier",
    returnQty: "Return qty",
    refund: "Refund received",
    refundTo: "Refund into",
    returned: "Returned",
    empty: "No purchases yet",
    emptyHint: "Record goods you receive to keep stock and supplier dues right.",
  },
  parties: {
    customers: "Customers",
    customersSubtitle: "Customer list and due khata.",
    suppliers: "Suppliers",
    suppliersSubtitle: "Who you buy from, and what you owe.",
    addCustomer: "Add customer",
    addSupplier: "Add supplier",
    contactPerson: "Contact person",
    creditLimit: "Credit limit",
    creditLimitHint: "Leave empty for no limit.",
    openingBalance: "Opening due",
    openingBalanceCustomerHint: "What this customer already owes you.",
    openingBalanceSupplierHint: "What you already owe this supplier.",
    balance: "Balance",
    owesYou: "Owes you",
    youOwe: "You owe",
    ledger: "Khata",
    collectDue: "Collect due",
    paySupplier: "Pay supplier",
    ageing: "Due ageing",
    d0_30: "0–30 days",
    d31_60: "31–60 days",
    d61_90: "61–90 days",
    d90p: "90+ days",
    group: "Group",
    terms: "Payment terms (days)",
    bin: "BIN",
    withBalanceOnly: "With dues only",
    txnRef: "Transaction ID",
    txnRefHint: "Required for bKash, Nagad, Rocket, Upay and card.",
    emptyCustomers: "No customers yet",
    emptySuppliers: "No suppliers yet",
    ledgerTypes: {
      opening: "Opening due",
      sale: "Sale",
      sale_return: "Return",
      purchase: "Purchase",
      purchase_return: "Return",
      payment: "Payment",
      adjustment: "Adjustment",
    },
  },
  pos: {
    extras: "Customer, discount & voucher",
    title: "Point of sale",
    scanOrSearch: "Scan barcode or search (F2)",
    cart: "Cart",
    emptyCart: "Scan or search to add products",
    customer: "Customer",
    walkIn: "Walk-in customer",
    findCustomer: "Find by name or phone",
    newCustomer: "New customer",
    subtotal: "Subtotal",
    discount: "Discount",
    billDiscount: "Bill discount",
    lineDiscount: "Item discount",
    vat: "VAT",
    sd: "SD",
    rounding: "Rounding",
    total: "Total",
    pay: "Pay (F9)",
    payTitle: "Take payment",
    addPayment: "Add another payment",
    received: "Received",
    change: "Change",
    due: "Due",
    complete: "Complete sale",
    hold: "Hold",
    held: "Held sales",
    resume: "Resume",
    clear: "Clear",
    registerClosed: "Open the register to start selling",
    openRegister: "Open register",
    openingFloat: "Cash in drawer now",
    closeRegister: "Close register",
    countedCash: "Cash counted",
    expected: "Expected",
    variance: "Difference",
    cashIn: "Cash in",
    cashOut: "Cash out",
    saleComplete: "Sale complete",
    newSale: "New sale",
    printReceipt: "Print receipt",
    percent: "%",
    amountTk: "৳",
    priceChanged: "Price changed",
    outOfStock: "Only {qty} in stock",
    shift: "Shift",
    salesInShift: "{count} sales",
    holdLabel: "Name for this cart",
  },
  sales: {
    title: "Sales",
    subtitle: "Invoices, returns and voids.",
    number: "Invoice",
    customer: "Customer",
    paid: "Paid",
    due: "Due",
    completed: "Completed",
    void: "Void",
    flagged: "Check stock",
    items: "Items",
    payments: "Payments",
    return: "Return items",
    returnTitle: "Return items",
    returnQty: "Return",
    refundTo: "Refund from",
    reduceDue: "Reduce customer's due",
    voidSale: "Void sale",
    voidHint: "Stock, payments and dues are reversed. Use this for sales entered by mistake.",
    returns: "Returns",
    empty: "No sales in this period",
  },
  money: {
    accounts: "Cash & accounts",
    accountsSubtitle: "Cash drawers, bKash, Nagad and bank accounts.",
    addAccount: "Add account",
    kind: "Type",
    kinds: {
      cash: "Cash",
      bkash: "bKash",
      nagad: "Nagad",
      rocket: "Rocket",
      upay: "Upay",
      card: "Card",
      bank: "Bank",
      courier: "Courier (to be paid out)",
    },
    accountName: "Account name",
    accountNumber: "Number",
    openingBalance: "Current balance",
    transfer: "Transfer",
    transferTitle: "Move money between accounts",
    fromAccount: "From",
    toAccount: "To",
    fee: "Fee",
    ownerMoney: "Owner money",
    capital: "Put money in",
    draw: "Take money out",
    cashBook: "Cash book",
    opening: "Opening",
    inflow: "In",
    outflow: "Out",
    closing: "Closing",
    reconciled: "Matched",
    paymentTypes: {
      sale: "Sale",
      sale_refund: "Refund",
      due_collection: "Due collected",
      purchase: "Purchase",
      purchase_refund: "Supplier refund",
      supplier_payment: "Supplier payment",
      expense: "Expense",
      income: "Income",
      transfer: "Transfer",
      transfer_fee: "Transfer fee",
      owner_capital: "Owner put in",
      owner_draw: "Owner took out",
      opening: "Opening balance",
      cash_in: "Cash in",
      cash_out: "Cash out",
    },
  },
  expenses: {
    title: "Expenses",
    subtitle: "Rent, bills, salaries and other costs.",
    add: "Add expense",
    category: "Category",
    paidFrom: "Paid from",
    empty: "No expenses in this period",
    voidTitle: "Cancel expense",
  },
  receipt: {
    invoice: "Invoice",
    date: "Date",
    cashier: "Served by",
    item: "Item",
    rate: "Rate",
    thankYou: "Thank you for shopping with us",
    changeGiven: "Change",
    paidBy: "Paid by",
    vatReg: "BIN",
  },
  settings: {
    title: "Settings",
    subtitle: "Business details, VAT and point-of-sale rules.",
    business: "Business",
    vat: "VAT",
    vatEnabled: "This business is VAT registered",
    vatHint: "Prints Mushak 6.3 invoices and keeps the VAT registers.",
    pricesIncludeVat: "Sale prices already include VAT",
    pos: "Point of sale",
    cashRounding: "Round bills to",
    roundNone: "No rounding",
    roundHalf: "Nearest ৳0.50",
    roundOne: "Nearest ৳1",
    allowNegativeStock: "Allow selling when stock shows zero",
    allowNegativeStockHint: "Shortfalls are settled when the next stock arrives.",
    discountLimit: "Cashier discount limit (%)",
    receiptNote: "Receipt footer",
    receiptNoteHint: "For example your return policy.",
    fiscalYearStart: "Fiscal year starts",
    numbers: "Numbers",
    digitsBangla: "Bangla digits (১২৩)",
    digitsLatin: "Latin digits (123)",
    saved: "Settings saved",
    onlineOrders: "Online orders",
    orderSms: "Text shoppers when their order is confirmed, sent, delivered or cancelled",
    orderSmsHint: "Uses your SMS credits. Test-mode orders never send SMS.",
  },
  reports: {
    title: "Reports",
    subtitle: "Sales, profit, payments and stock.",
    sales: "Sales",
    payments: "Payments",
    balance: "Balance sheet",
    trial: "Trial balance",
    ledger: "Ledger",
    pnl: "Profit & loss",
    stock: "Stock",
    groupBy: "Group by",
    groups: {
      day: "Day",
      item: "Product",
      category: "Category",
      cashier: "Cashier",
      customer: "Customer",
    },
    invoices: "Invoices",
    gross: "Gross",
    net: "Net",
    profit: "Profit",
    margin: "Margin",
    exportCsv: "Export (Excel)",
    range: "Period",
    thisMonth: "This month",
    lastMonth: "Last month",
    custom: "Custom",
    salesLine: "Sales",
    returnsLine: "Returns",
    netSales: "Net sales",
    cogs: "Cost of goods sold",
    grossProfit: "Gross profit",
    inventoryLoss: "Stock losses",
    expenses: "Expenses",
    paymentFees: "Payment fees",
    otherIncome: "Other income",
    netProfit: "Net profit",
    valuation: "Stock value",
    slowMoving: "Not sold in {days} days",
    atCost: "At cost",
    atRetail: "At sale price",
    lastSold: "Last sold",
    never: "Never",
    count: "Count",
    walkIn: "Walk-in",
    reorder: "Reorder",
  },
  reorder: {
    hint: "Items at or below their reorder point, with how much to order to cover your sales. Based on the last 30 days.",
    leadTime: "Delivery time",
    leadOption: "Delivery in {n} days",
    cover: "Order enough for",
    coverOption: "Enough for {n} days",
    supplier: "Supplier",
    noSupplier: "Never bought",
    onHand: "In stock",
    lasts: "Lasts",
    order: "Order",
    estimated: "Estimated cost",
    days: "{n} days",
    outNow: "Out now",
    empty: "Nothing needs reordering right now.",
  },
  vat: {
    title: "VAT",
    subtitle: "NBR registers and invoices.",
    salesRegister: "Sales register (Mushak 6.2)",
    purchaseRegister: "Purchase register (Mushak 6.1)",
    creditNotes: "Credit notes",
    value: "Value",
    sd: "SD",
    vat: "VAT",
    buyer: "Buyer",
    seller: "Seller",
    bin: "BIN",
    notRegistered: "Turn on VAT in Settings with your BIN to use VAT registers.",
    mushak63: "Mushak 6.3 tax invoice",
    taxInvoice: "Tax invoice",
    rule: "[See rule 40, sub-rule (1), clauses (c) and (f)]",
    issuer: "Name of registered person",
    issuerBin: "BIN",
    issuerAddress: "Address of issuing",
    buyerName: "Buyer's name",
    buyerBin: "Buyer's BIN",
    destination: "Supply destination",
    vehicle: "Vehicle type and number",
    challanNo: "Challan number",
    issueDate: "Date of issue",
    issueTime: "Time of issue",
    sl: "Sl.",
    description: "Description of goods",
    unitOfSupply: "Unit of supply",
    quantity: "Quantity",
    unitPrice: "Unit price (Tk)",
    totalPrice: "Total price (Tk)",
    sdRate: "SD rate",
    sdAmount: "SD (Tk)",
    vatRate: "VAT rate",
    vatAmount: "VAT (Tk)",
    totalWithTaxes: "Value incl. all duties and taxes (Tk)",
    signature: "Signature of authorised person",
    printMushak: "Print Mushak 6.3",
  },
  team: {
    title: "Team",
    subtitle: "Who can use this business, and what they can do.",
    invite: "Invite staff",
    inviteHint: "They get an email and join when they sign in with this Gmail.",
    inviteEmailHint: "Gmail only, e.g. name@gmail.com",
    pending: "Invited",
    expires: "Invite expires {when}",
    revoke: "Cancel invite",
    remove: "Remove",
    allBranches: "All branches",
    branches: "Branches",
    you: "You",
  },
  branches: {
    title: "Branches",
    add: "Add branch",
    code: "Short code",
    codeHint: "2–6 letters, printed on invoice numbers. Cannot change later.",
    close: "Close branch",
    main: "Main",
  },
  billing: {
    title: "Plan & billing",
    subtitle: "Your plan, usage, SMS credits and invoices.",
    current: "Current plan",
    renews: "Paid until {date}",
    grace: "Payment overdue. Renew by {date} to keep paid features.",
    lapsed: "Your plan has ended. Free limits apply until you renew.",
    usage: "Usage",
    unlimited: "Unlimited",
    of: "{used} of {limit}",
    users: "Users",
    branches: "Branches",
    items: "Products",
    monthly: "Monthly",
    yearly: "Yearly",
    perMonth: "/month",
    perYear: "/year",
    choose: "Choose {plan}",
    renew: "Renew",
    notOnSale: "Price not yet available",
    contactSales: "Contact us",
    smsCredits: "SMS credits",
    buyCredits: "Buy {credits} credits",
    invoices: "Invoices",
    invoice: "Invoice",
    email: "Email for the receipt",
    receiptTo: "The receipt goes to {email}.",
    phone: "Mobile number for the payment",
    phoneHint: "The payment page needs it; bKash and Nagad may send a confirmation.",
    payWith: "You will pay on a secure payment page with bKash, Nagad, Rocket or another method.",
    paid: "Payment received. Thank you!",
    failed: "The payment did not go through. You were not charged.",
    cancelled: "Payment cancelled.",
    pending:
      "Your payment is being checked. Your plan or credits will be added as soon as it is confirmed.",
    offline: "Online payment is not available right now. Please contact support.",
    features: {
      vat: "VAT invoices (Mushak 6.3)",
      offline: "Offline selling",
      multi_branch: "Multiple branches",
      custom_domain: "Own website domain",
      api: "Developer API",
      couriers: "Courier booking",
      advanced_reports: "Advanced reports",
    },
    upgrade: "Upgrade",
    billedTo: "Billed to",
    issuedBy: "Issued by",
    refunded: "Refunded",
  },
  sms: {
    reminders: "Send due reminders",
    remindersHint: "Every customer with a due and a phone number gets an SMS.",
    template: "Message",
    placeholders: "Use {name}, {amount} and {shop}.",
    preview: "Preview",
    cost: "{count} messages · about {credits} credits",
    balance: "Credits left: {balance}",
    send: "Send",
    sent: "{count} messages queued",
    receipt: "SMS receipt",
    noCredits: "Not enough SMS credits",
    status: {
      queued: "Queued",
      sent: "Sent",
      failed: "Failed",
    },
  },
  import: {
    title: "Import",
    items: "Import products",
    customers: "Import customers",
    suppliers: "Import suppliers",
    choose: "Choose an Excel (.xlsx) or CSV file",
    template: "Download template",
    map: "Match the columns",
    skip: "Don't import",
    rows: "{count} rows",
    errors: "{count} rows need fixing",
    run: "Import {count} rows",
    done: "{count} rows imported",
    row: "Row {n}",
    fields: {
      nameEn: "Name (English)",
      nameBn: "Name (Bangla)",
      category: "Category",
      brand: "Brand",
      baseUnit: "Unit",
      salePrice: "Sale price",
      mrp: "MRP",
      sku: "SKU",
      barcode: "Barcode",
      vatRate: "VAT %",
      reorderLevel: "Low-stock alert",
      openingQty: "Opening quantity",
      openingCost: "Cost per unit",
      batchNo: "Batch",
      expiryDate: "Expiry date",
      name: "Name",
      phone: "Phone",
      address: "Address",
      openingBalance: "Opening due",
    },
  },
  starter: {
    title: "Add from catalog",
    subtitle: "Pick common products and set your prices.",
    empty: "No catalog is available for your business type yet.",
    add: "Add {count} products",
  },
  admin: {
    title: "Zeeta admin",
    metrics: "Overview",
    tenants: "Businesses",
    pricing: "Pricing",
    catalog: "Starter catalogs",
    audit: "Audit log",
    errors: "Errors",
    noErrors: "No errors in the last 7 days.",
    errorCount: "{count} times · {tenants} businesses",
    signups: "New in 30 days",
    paying: "Paying",
    revenue: "Revenue, 30 days",
    smsSent: "SMS sent, 30 days",
    suspended: "Suspended",
    suspend: "Suspend",
    reactivate: "Reactivate",
    setPlan: "Set plan",
    grantSms: "Grant SMS credits",
    reason: "Reason (kept in the audit log)",
    periodEnd: "Paid until",
    owner: "Owner",
    source: "Source of the data",
    uploadCatalog: "Upload catalog file",
    replaceCatalog: "Replace {pack} catalog with {count} rows",
    readOnly: "Support access is read-only.",
    staff: "Staff",
    signins: "Sign-ins",
    support: "Support visits",
    openAdmin: "Platform admin",
    stepUpTitle: "Confirm it is you",
    stepUpHint:
      "The admin opens after you enter a code we email to {email}. It stays open for up to 8 hours, and closes after 30 minutes without use.",
    stepUpSend: "Email me a code",
    stepUpVerify: "Open the admin",
    lock: "Lock admin",
    staffEmail: "Gmail address",
    staffEmailHint: "They must have signed in to Zeeta once.",
    role: "Role",
    roleAdmin: "Admin (full control)",
    roleSupport: "Support (look only)",
    addStaff: "Add to staff",
    removeStaff: "Remove from staff",
    removeConfirm: "Remove {name} from the admin staff? Their admin access ends at once.",
    lastUsed: "Last used the admin",
    you: "You",
    suspicious: "Many failed sign-ins from these addresses in the last hour",
    failures: "{n} failures",
    search: "Search",
    eventsSearch: "Search by email, phone or IP",
    eventType: "Event",
    allEvents: "All events",
    who: "Who",
    events: {
      otp_requested: "Code requested",
      otp_failed: "Wrong code",
      signin: "Signed in",
      signout: "Signed out",
      session_revoked: "Device signed out",
      user_created: "Account created",
      password_set: "Password set",
      password_failed: "Wrong password",
      google_signin: "Signed in with Google",
      email_changed: "Email changed",
      phone_verified: "Phone verified",
      admin_step_up: "Opened the admin",
      admin_step_up_failed: "Wrong admin code",
      admin_pin_unlock: "Admin unlocked with PIN",
      admin_pin_failed: "Wrong admin PIN",
      admin_pin_set: "Admin PIN set",
      admin_pin_removed: "Admin PIN removed",
    },
    business: "Business",
    reasonShort: "Reason",
    until: "Until",
    modeRead: "Look only",
    modeWrite: "Can make changes",
    ended: "Ended",
    endedByBusiness: "Ended by the business",
    openBusiness: "Open",
    endVisit: "End",
    supportTitle: "Open as support",
    supportHint:
      "See this business as its owner does, to help them. The owner is emailed, the visit ends on its own, and everything is logged.",
    supportReason: "Why (the owner sees this)",
    duration: "How long",
    minutes: "{n} minutes",
    mode: "Access",
    startVisit: "Open the business",
    tempPassword: "Temporary password",
    tempIssued: "Temporary password for {email}. It is shown only now:",
    tempHint:
      "It works once within 24 hours; they must choose their own password after signing in with it.",
    account: "My account",
    lockHint:
      "Closes the admin on this browser. You stay signed in to Zeeta; reopening needs a new emailed code.",
    groupPlatform: "Platform",
    businessTypes: "Business types",
    announcements: "Announcements",
    maintenance: "Maintenance",
    securitySettings: "Security & sign-up",
    ai: "AI",
    barcodes: "Barcode library",
    groupSettings: "Settings",
    groupSecurity: "Security",
    groupYou: "You",
    modeFull: "Full access (as owner)",
    users: "Users",
    billing: "Billing",
  },
  pwa: {
    updateReady: "A new version of Zeeta is ready.",
    reload: "Reload",
  },
  offline: {
    online: "Online",
    offline: "Offline",
    pending: "{count} sales waiting to sync",
    syncing: "Syncing…",
    syncNow: "Sync now",
    syncFailed: "{count} sales need attention",
    snapshotAge: "Product list updated {when}",
    refresh: "Refresh product list",
    provisional: "Provisional no.",
    signOutBlocked: "Sales are still waiting to sync. Connect to the internet first.",
    offlineSale: "Saved offline. It will sync automatically.",
  },
  account: {
    title: "Account and security",
    subtitle: "How you sign in to Zeeta.",
    emailTitle: "Gmail",
    emailHint: "You sign in with this address, and invitations and receipts go to it.",
    addEmailNow: "Add your Gmail. Signing in with a phone number is being retired.",
    changeEmail: "Change to another Gmail",
    passwordTitle: "Password",
    passwordHint: "Optional. Lets you sign in with your Gmail and a password instead of a code.",
    passwordSaved: "Password saved. Other devices were signed out.",
    currentPassword: "Current password",
    newPassword: "New password",
    confirmPassword: "Type it again",
    passwordRules: "At least 10 characters. Not your email or a common password.",
    passwordMismatch: "The two passwords are different",
    setPassword: "Set password",
    changePassword: "Change password",
    reauth:
      'For safety, enter your current password, or use "Forgot your current password?" below to get an email code.',
    forgotCurrent: "Forgot your current password?",
    phoneTitle: "Mobile number",
    phoneHint: "Optional. A verified number can get SMS alerts.",
    changePhone: "Use another number",
    verifyPhone: "Send code",
    verified: "Verified",
    notVerified: "Not verified",
    banner: "Add your Gmail to keep access to your account.",
    bannerAction: "Add Gmail",
    forgotHint: 'Forgot it? Use "Forgot your current password?" below.',
    mustChangeTitle: "Choose your own password",
    mustChangeHint:
      "You signed in with a temporary password. Choose a password only you know before you go on.",
  },
  sessions: {
    title: "Signed-in devices",
    subtitle: "Sign out of devices you don't recognise.",
    thisDevice: "This device",
    trusted: "Remembered",
    lastActive: "Last active {when}",
    signedIn: "Signed in {when}",
    revoke: "Sign out",
    unknownDevice: "Unknown device",
  },
  legal: {
    productOf: "A product of {company}",
    terms: "Terms",
    privacy: "Privacy",
    nav: "Legal",
    agreePrefix: "By continuing you agree to the",
    and: "and",
    agreeSuffix: " Policy.",
  },
  orders: {
    markReturned: "Parcel came back",
    returnedHint:
      "Puts every item back in stock and clears what the customer owed for this order, including the delivery charge.",
    returnReasonPlaceholder: "e.g. Customer refused, wrong address",
    title: "Online orders",
    subtitle: "Orders from your website and apps. Stock is held until you ship or cancel.",
    testMode: "Show test orders",
    testModeHint:
      "You are looking at test-mode orders. They never touch your real stock or accounts.",
    testOrder: "Test order",
    search: "Order number, phone or name",
    empty: "No orders here",
    emptyHint: "Orders placed through your API keys or website appear here.",
    number: "Order",
    customer: "Customer",
    district: "District",
    placed: "Placed",
    prepaid: "Prepaid",
    cod: "Cash on delivery",
    status: {
      pending: "New",
      confirmed: "Confirmed",
      packed: "Packed",
      shipped: "Shipped",
      delivered: "Delivered",
      cancelled: "Cancelled",
      returned: "Returned",
      all: "All",
    },
    channel: { api: "API", storefront: "Website", manual: "Manual" },
    confirm: "Confirm order",
    pack: "Mark packed",
    ship: "Mark shipped",
    deliver: "Mark delivered",
    cancel: "Cancel order",
    items: "Items",
    subtotal: "Value before VAT",
    sd: "SD",
    vat: "VAT",
    deliveryCharge: "Delivery charge",
    paid: "Paid",
    toCollect: "To collect",
    statusTitle: "Status",
    reason: "Reason",
    viewSale: "View the sale",
    shipTo: "Deliver to",
    externalRef: "Their reference",
    shipHint: "The items leave your stock now and the sale is recorded against the customer.",
    receivedIn: "Money received in",
    txnRef: "Transaction ID",
    collectCod: "I received {amount} on delivery",
    leaveDue: "The amount stays as the customer's due.",
    alreadyPaid: "This order is already paid.",
    cancelHint: "The held stock is released. The customer is told by SMS if order SMS is on.",
  },
  accounts: {
    asAt: "As at {date}",
    balanced: "Balanced",
    notBalanced: "Not balanced — please contact support",
    code: "Code",
    debit: "Debit",
    credit: "Credit",
    balance: "Balance",
    account: "Account",
    entry: "Entry",
    opening: "Opening balance",
    closing: "Closing balance",
    assets: "What the business owns",
    liabilities: "What it owes",
    equity: "Owner's share",
    totalAssets: "Total assets",
    totalLiabilitiesEquity: "Total owed and owner's share",
    truncated: "Showing the first 2,000 entries. Choose a shorter period to see the rest.",
    refs: {
      sale: "Sale",
      sale_return: "Sale return",
      sale_void: "Sale cancelled",
      purchase: "Purchase",
      purchase_return: "Purchase return",
      payment: "Payment",
      expense: "Expense",
      expense_void: "Expense cancelled",
      transfer: "Transfer",
      order: "Online order",
      order_return: "Returned order",
      vat_return: "VAT return filed",
      vat_payment: "VAT deposit",
    },
  },
  vatReturn: {
    title: "Monthly return (Mushak 9.1)",
    period: "Tax period",
    file: "File return",
    fileTitle: "File the return for {period}",
    fileHint:
      "Freezes these figures and moves the period's VAT and SD into VAT payable. Submit the same figures on the NBR e-VAT portal.",
    submissionRef: "NBR submission number",
    submissionRefHint: "From the e-VAT portal, after you submit online.",
    recordDeposit: "Record treasury deposit",
    paidFrom: "Paid from",
    challan: "Challan / e-payment no.",
    filedOn: "Filed {date}",
    notFiled: "Not filed yet",
    inProgress: "Month in progress",
    changed: "Entries for this month changed after filing; an amended return may be needed.",
    note: "Note",
    item: "Item",
    supplies: "Sales (output tax)",
    zeroOrExempt: "Zero-rated and exempt supplies",
    standard: "Standard rate (15%)",
    reduced: "Reduced rate ({rate}%)",
    totalOutput: "Total output tax",
    purchases: "Purchases (input tax)",
    localPurchases: "Local purchases with VAT credit",
    totalInput: "Total purchases and input tax credit",
    adjustments: "Adjustments",
    debitNotes: "Debit notes (purchase returns): increasing",
    creditNotes: "Credit notes (sale returns): decreasing",
    net: "Net tax",
    netVat: "Net VAT payable",
    netSd: "Net SD payable",
    carriedForward: "Credit carried forward from earlier periods",
    toDeposit: "To deposit for this period",
    disclaimer:
      "Computed from your sales, purchases and returns. Note numbers follow Mushak 9.1; check the figures with your VAT consultant before submitting.",
  },
  storefront: {
    title: "Online shop",
    subtitle: "Your own website, with products and stock straight from Zeeta.",
    setupTitle: "Open your online shop",
    setupHint:
      "Pick an address and a look. You can change everything later, and nothing is public until you publish.",
    address: "Shop address",
    addressHint: "3 to 40 letters, digits or hyphens, like rahim-store.",
    addressChangeHint: "Changing the address breaks links you have already shared.",
    theme: "Theme",
    themes: {
      bazaar: "Bazaar",
      boutique: "Boutique",
      apothecary: "Apothecary",
      catalog: "Catalog",
    },
    themeHints: {
      bazaar: "Bold and fast, like a busy market. For groceries and general shops.",
      boutique: "Editorial, big photos. For fashion, electronics and gifts.",
      apothecary: "Calm and trustworthy. For pharmacies and health shops.",
      catalog: "A showroom: products and prices, no online ordering.",
    },
    create: "Create my shop",
    live: "Live",
    offline: "Not published",
    unpublished: "Unpublished changes",
    saveDraft: "Save draft",
    saved: "Saved",
    publish: "Publish",
    takeOffline: "Take offline",
    preview: "Preview",
    previewSaveHint: "Save to see changes",
    mobile: "Phone view",
    desktop: "Computer view",
    reload: "Reload preview",
    tabs: {
      sections: "Sections",
      design: "Look & SEO",
      delivery: "Delivery & checkout",
      address: "Address",
      stats: "Visits & sales",
    },
    sections: {
      announcement: "Announcement bar",
      highlights: "Highlights",
      hero: "Hero",
      categories: "Category tiles",
      products: "Product list",
      banner: "Banner",
      text: "Text",
      contact: "Contact",
    },
    sources: {
      newest: "Newest products",
      category: "From a category",
      manual: "Hand-picked",
    },
    heroLayouts: {
      split: "Photo beside the words",
      full: "Photo behind the words",
      text: "Words on your colour",
    },
    highlightsHint:
      "Up to four short promises shown under the hero. Leave them empty and the shop shows cash on delivery, your delivery charges, free delivery and your phone number.",
    fields: {
      layout: "Layout",
      promise: "Promise {n}",
      promiseBody: "A short line under it",
      text: "Text",
      heading: "Heading",
      subheading: "Subheading",
      image: "Image",
      button: "Button text",
      buttonLink: "Button link",
      title: "Title",
      body: "Text",
      categories: "Categories",
      categoriesHint: "Leave all unticked to show every category that has products online.",
      source: "Show",
      limit: "How many",
      showPhone: "Show phone number",
      showAddress: "Show address",
      hours: "Opening hours",
    },
    link: "Link",
    linkHint: "A shop page like /products or /c/…, or a full https:// link.",
    addSection: "Add a section",
    hide: "Hide section",
    show: "Show section",
    moveUp: "Move up",
    moveDown: "Move down",
    searchItems: "Search products to add",
    pickedItem: "Product {n}",
    bangla: "Bangla",
    english: "English",
    color: "Brand colour",
    logo: "Logo",
    tagline: "Tagline",
    defaultLanguage: "Language shoppers see first",
    seoTitle: "Title in search results",
    seoDescription: "Description in search results",
    footerNote: "Footer note",
    catalogHint: "Catalog only: shoppers see products and stock but cannot order online.",
    branch: "Sell stock from",
    branchHint: "Online orders reserve and ship stock from this branch.",
    mainBranch: "Main branch",
    insideDhaka: "Delivery inside Dhaka",
    outsideDhaka: "Delivery outside Dhaka",
    districtRates: "Different charge for some districts",
    district: "District",
    freeDelivery: "Free delivery on bigger orders",
    freeAbove: "Free delivery from",
    minOrder: "Minimum order",
    minOrderHint: "0 means no minimum.",
    verifyPhone: "Verify the shopper's phone with a code",
    verifyPhoneHint: "Cuts fake cash-on-delivery orders. Each code uses one SMS credit.",
    hideOutOfStock: "Hide products that are out of stock",
    shopperAccounts: "Let shoppers sign in to see their orders",
    shopperAccountsHint:
      "They sign in with a phone code (one SMS credit each), and checkout fills in their last address.",
    checkoutNote: "Note on the checkout page",
    stats: {
      hint: "Visitors are counted once a day per browser. Search engines and your previews are not counted.",
      period: "Period",
      lastDays: "Last {n} days",
      visitors: "Visitors",
      views: "{n} page views",
      orders: "Orders",
      conversion: "Visitors who ordered",
      revenue: "Order value",
      averageOrder: "Average order",
      byDay: "By day",
      day: "Day",
      empty: "No visits yet. Share your shop link to get started.",
      mostViewed: "Most viewed",
      bestSelling: "Best selling online",
    },
    domains: {
      title: "Your own domain",
      hint: "Use a domain you own, like www.myshop.com.bd. The certificate is issued and renewed for you.",
      planNeeded: "Your own domain comes with the Business plan.",
      add: "Add a domain",
      check: "Check now",
      makePrimary: "Make main address",
      primary: "Main address",
      dnsHint:
        "At your domain provider, add these records. It can take a few minutes to a day to work; Zeeta checks every hour.",
      type: "Type",
      name: "Name",
      value: "Value",
      status: { pending: "Waiting for DNS", active: "Working", failed: "Not working" },
    },
  },
  media: {
    add: "Add photo",
    replace: "Replace",
    uploading: "Uploading…",
    main: "Main",
    makeMain: "Make main photo",
  },
  couriers: {
    title: "Couriers",
    subtitle:
      "Book Pathao, Steadfast and RedX parcels from orders and track the cash they collect.",
    connect: "Connect courier",
    connectHint:
      "Use your own merchant account. Zeeta checks the details with the courier before saving them, encrypted.",
    provider: "Courier",
    providers: { pathao: "Pathao", steadfast: "Steadfast", redx: "RedX" },
    where: {
      steadfast: "Steadfast merchant panel → API: copy the API key and secret key.",
      pathao:
        "Pathao merchant panel → Developers API: copy the client ID and secret. Use the email and password you sign in to Pathao with.",
      redx: "RedX merchant panel → Developer API: create an access token and paste it here.",
    },
    labelName: "Name for this account",
    fields: {
      apiKey: "API key",
      secretKey: "Secret key",
      clientId: "Client ID",
      clientSecret: "Client secret",
      username: "Pathao login email",
      password: "Pathao password",
      accessToken: "Access token",
    },
    sandbox: "Courier's test (sandbox) server",
    planNeeded: "Courier integrations come with the Business plan.",
    upgrade: "See plans",
    emptyTitle: "No courier connected",
    emptyHint:
      "Connect your Pathao, Steadfast or RedX merchant account to book parcels from orders.",
    disabled: "Turned off",
    turnOff: "Turn off",
    turnOn: "Turn on",
    dueFrom: "Cash this courier owes you: {amount}",
    store: "Pickup store",
    storeHint: "Where the courier collects parcels.",
    defaultStore: "Account's default",
    webhookSetup: "Status updates (webhook)",
    webhookHow: {
      steadfast:
        "Steadfast panel → Webhook: paste the URL as Callback URL and the secret as Auth Token (Bearer).",
      pathao:
        "Pathao panel → Developers API → Webhook: paste the URL and the secret, then press Integrate.",
      redx: "Send this URL to RedX support as your callback URL. Zeeta also checks parcels every hour.",
    },
    webhookUrl: "Webhook URL",
    webhookSecret: "Webhook secret",
    toBePaid: "Delivered, not paid out yet",
    nothingDue: "Nothing waiting to be paid out.",
    recordPayout: "Record payout",
    payoutHint:
      "Tick the parcels this payment covers. Whatever the courier kept is booked as courier charges.",
    received: "Amount received",
    receivedIn: "Received in",
    collectedTotal: "Cash collected for these parcels: {amount}",
    feesWillBe: "Courier charges recorded: {amount}",
    tooMuch: "That is more than the cash collected for these parcels.",
    reference: "Payment reference",
    order: "Order",
    statusCol: "Status",
    collectedShort: "Collected",
    feeShort: "Charge",
    unknown: "Not reported",
    onTheWay: "On the way",
    payouts: "Payouts",
    date: "Date",
    parcels: "Parcels",
    shipments: "Courier",
    book: "Book courier",
    bookHint: "The courier creates a consignment, and the order is marked shipped.",
    account: "Courier account",
    city: "City",
    zone: "Zone",
    area: "Delivery area",
    auto: "Let the courier detect",
    cod: "Cash to collect",
    codHint: "Still owed on this order: {amount}",
    weight: "Weight (grams)",
    note: "Note for the rider",
    refresh: "Check status now",
    label: "Print label",
    codAmount: "To collect {amount}",
    collected: "collected {amount}",
    fee: "charge {amount}",
    returnedHint:
      'The parcel came back. Use "Parcel came back" above to restock the goods and clear the customer\'s due.',
    enterCollected: "Cash the courier collected",
    status: {
      booked: "Booked",
      in_transit: "On the way",
      hold: "On hold",
      delivered: "Delivered",
      partial: "Partly delivered",
      returned: "Returned",
      cancelled: "Cancelled",
    },
  },
  developers: {
    title: "Developers",
    subtitle: "API keys and webhooks for your own website, app or integrations.",
    docs: "API documentation",
    liveNeedsPlan:
      "Live keys and webhooks need the Business plan. You can build and test with test mode on any plan.",
    keys: "API keys",
    keysHint: "Secret keys stay on your server. Publishable keys can be used on your website.",
    createKey: "Create key",
    noKeys: "No API keys yet",
    key: "Key",
    access: "Access",
    lastUsed: "Last used",
    never: "Never",
    expires: "Expires {when}",
    revoke: "Revoke",
    revokeConfirm: "Revoke “{name}”? Anything using it stops working within a minute.",
    webhooks: "Webhooks",
    webhooksHint: "We send events to your URL as they happen, signed so you can trust them.",
    addEndpoint: "Add endpoint",
    testSent: "Test event sent. Check the deliveries list.",
    noEndpoints: "No webhook endpoints yet",
    disabled: "Disabled",
    deliveries: "Deliveries",
    sendTest: "Send test",
    signingSecret: "Signing secret",
    disable: "Disable",
    enable: "Enable",
    deleteConfirm: "Delete this endpoint and its delivery history?",
    testData: "Test data",
    testDataHint:
      "Test mode works on a copy of your products, branches, payment accounts and stock. Reset it to start again from your current live data.",
    resetTestData: "Reset test data",
    createTestData: "Create test data",
    resetConfirm: "All test orders, sales and customers are deleted and copied fresh from live.",
    mode: { live: "Live", test: "Test" },
    kind: { publishable: "Publishable", secret: "Secret" },
    kindHint: {
      publishable:
        "For your website's browser code. Reads products and places cash-on-delivery orders.",
      secret: "For your server. Choose exactly what it may do.",
    },
    keyCreated: "Your new key",
    secretOnce: "Copy it now. For your security we will never show this secret key again.",
    nameHint: "Where it is used, e.g. “Shop website”",
    modeLabel: "Mode",
    permissions: "Permissions",
    origins: "Allowed websites",
    originsHint: "One origin per line, e.g. https://shop.example.com",
    expiry: "Expires",
    days: "In {n} days",
    endpointCreated: "Endpoint added",
    secretHint: "Use this secret to check the Zeeta-Signature header on each request.",
    url: "Endpoint URL",
    events: "Events",
    rotate: "Rotate secret",
    rotateConfirm: "Your receiver must switch to the new secret straight away. Continue?",
    noDeliveries: "Nothing has been sent to this endpoint yet.",
    response: "Response",
    attempts: "{n} attempts",
    nextAttempt: "retry {when}",
    resend: "Resend",
    scope: {
      catalog_read: "Read products and stock",
      customers_read: "Read customers",
      customers_write: "Create customers",
      orders_read: "Read orders",
      orders_create: "Place orders",
      orders_write: "Place and cancel orders",
      sales_read: "Read sales",
    },
    event: {
      label: "Event",
      order_created: "A new order is placed",
      order_updated: "An order changes status",
      sale_completed: "A sale is recorded, at the counter or from an order",
      stock_low: "An item falls to its reorder level",
      item_updated: "A product's details or price change",
      customer_created: "A new customer is added",
    },
    delivery: { pending: "Retrying", succeeded: "Delivered", failed: "Failed" },
  },
  apiDocs: {
    title: "API reference",
    introduction: "Introduction",
    sdk: "TypeScript SDK",
    sdkHint: "A typed client with pagination, retries and idempotency built in.",
    webhooks: "Webhooks",
    webhooksHint: "Verify every request before you trust it.",
    objects: "Objects",
    baseUrl: "Base URL",
    scope: "Permission",
    publishableOk: "Works with publishable keys",
    parameters: "Parameters",
    body: "Request body",
    returns: "Returns",
    list: "a list",
    required: "required",
    default: "default",
  },
  wholesale: {
    title: "Wholesale",
    subtitle: "Price lists for dealers and retailers, sales reps and their commission.",
    priceLists: "Price lists",
    addList: "New price list",
    listNameEn: "Name",
    listNameBn: "Name in Bangla",
    listNameHint: "For example Dealer, Retailer or Wholesale.",
    listActive: "In use",
    inactive: "Not in use",
    listCounts: "{items} items · {customers} customers",
    listsEmpty: "No price lists yet.",
    listsEmptyTitle: "Sell at different prices to different customers",
    listsEmptyHint:
      "Make a price list, set its prices, then put customers on it. They get those prices at the till automatically, even offline.",
    gridHint: "Items not on this list sell at their normal price.",
    gridEmpty: "No prices on this list yet. Search for an item above to add it.",
    addItem: "Add an item to this list…",
    normalPrice: "Normal price",
    listPrice: "List price",
    savePrices: "Save changes ({count})",
    priceList: "Price list",
    priceListHint: "This customer gets the list's prices at the till.",
    normalPrices: "Normal prices",
    onList: "Buying on the {name} price list",
    reps: "Sales reps",
    addRep: "New sales rep",
    rep: "Sales rep",
    noRep: "No sales rep",
    repLabel: "Rep: {name}",
    repActive: "Still working",
    repsEmpty: "No sales reps yet",
    repsEmptyHint:
      "Add the people who sell for you, then pick one at the till to credit them with the sale.",
    commission: "Commission %",
    commissionHint: "Paid on sales after discounts, without VAT, less returns.",
    commissionInvalid: "Enter a percentage between 0 and 50",
    repReport: "Commission report",
    invoices: "Invoices",
    netSales: "Net sales",
    commissionDue: "Commission",
    reportEmpty: "No sales credited to a rep in this period.",
    reportNote:
      "Net sales exclude VAT and SD and subtract returns made in the period. Pay commission from Expenses.",
    challan: "Delivery challan",
    challans: "Delivery challans",
    makeChallan: "Make challan",
    challanNo: "Challan no.",
    invoiceNo: "Invoice no.",
    deliverTo: "Deliver to",
    vehicleNo: "Vehicle no.",
    driver: "Driver",
    driverName: "Driver's name",
    driverPhone: "Driver's phone",
    goods: "Goods",
    receivedBy: "Received by",
    carriedBy: "Carried by",
    authorisedBy: "Authorised by",
  },
  loyalty: {
    title: "Loyalty & vouchers",
    subtitle: "Reward regular customers with points and run discount codes.",
    points: "Loyalty points",
    pointsHint: "Customers earn points on what they spend and use them as a discount next time.",
    on: "Turn on",
    spendPerPoint: "Spend for 1 point",
    pointValue: "1 point is worth",
    minRedeemLabel: "Fewest points to use at once",
    example: "A customer who spends {spend} earns {points} points, worth {value} off a later bill.",
    vouchers: "Vouchers",
    vouchersHint:
      "Discount codes any cashier can apply, such as EID500. They do not need discount permission.",
    vouchersEmpty: "No vouchers yet.",
    addVoucher: "New voucher",
    voucherCode: "Voucher code",
    codeHint: "3–24 letters, digits or -. Customers give this at the till.",
    kind: "Discount type",
    kindAmount: "Fixed amount",
    kindPercent: "Percentage",
    discountAmount: "Amount off",
    discountPercent: "Percent off",
    minSpend: "Minimum bill",
    minSpendShort: "On bills of {amount} or more",
    maxDiscount: "Most it can take off",
    starts: "First day",
    ends: "Last day",
    usageLimit: "How many times it can be used",
    oncePerCustomer: "Each customer can use it once",
    voucherActive: "In use",
    discount: "Discount",
    valid: "Valid",
    always: "Always",
    used: "Used",
    off: "Off",
    expired: "Expired",
    usedUp: "Used up",
    scheduled: "Not started",
    apply: "Apply",
    voucher: "Voucher",
    voucherApplied: "Voucher {code}",
    voucherMinSpend: "Needs a bill of at least {amount}",
    usePoints: "Use points (has {points}, worth {value})",
    minRedeem: "Use at least {points} points",
    pointsLine: "Points",
    pointsShort: "{n} points",
    pointsUsed: "{n} points used",
    pointsEarned: "You earned {n} points on this bill",
    customerPoints: "{n} points",
    worth: "worth {value}",
    adjust: "Adjust points",
    adjustPoints: "Points to add",
    adjustHint: "Use a minus sign to take points away, e.g. -20.",
    reason: "Reason",
    pointsCol: "Points",
    types: {
      earn: "Earned",
      redeem: "Used",
      reverse: "Reversed",
      adjust: "Adjusted",
    },
  },
  quotations: {
    title: "Quotations",
    subtitle: "Price offers for customers, turned into a sale when they agree.",
    new: "New quotation",
    empty: "No quotations yet",
    emptyHint: "Send a customer a priced offer, then sell it in one click when they accept.",
    number: "Quotation",
    status: "Status",
    all: "All",
    open: "Open",
    accepted: "Sold",
    cancelled: "Cancelled",
    expired: "Expired",
    validUntil: "Valid until",
    prospect: "Or the name of someone not yet a customer",
    price: "Price",
    totalHint: "Total before VAT; the quotation shows the exact figure.",
    save: "Save quotation",
    sheetTitle: "Quotation",
    to: "To",
    vatIncluded: "Prices include VAT.",
    vatAdded: "VAT is added to these prices.",
    cancel: "Cancel quotation",
    convert: "Sell this quotation",
    convertConfirm: "Make the sale",
    convertHint: "The sale uses the quoted prices and takes the goods out of stock.",
    receivedIn: "Payment received in",
    dueNote: "{amount} will be added to the customer's due.",
    prospectMustPay: "Add this person as a customer to sell on due, or take the full amount now.",
    viewSale: "View the sale",
  },
  serials: {
    track: "Record a serial or IMEI number for each one sold",
    warrantyMonths: "Warranty (months)",
    warrantyHint: "From the day it is sold. 0 for none.",
    entry: "Serial or IMEI number",
    short: "IMEI/SN",
    missing: "Scan or type {n} IMEI / serial",
    payBlocked: "Enter {n} more serial or IMEI number before taking payment.",
    offlineLater: "Offline: record the serial numbers on the invoice later.",
    warrantyUntil: "Warranty until {date}",
    record: "Record IMEI / serial",
    recordHint: "Enter {n}, one per line.",
    purchaseHint: "IMEI or serial numbers, one per line",
    count: "{n} entered",
    check: "Warranty check",
    checkHint: "Scan or type the IMEI or serial number",
    notFound: "No product with this number has been bought or sold here.",
    inStock: "In stock",
    covered: "Under warranty",
    expired: "Warranty over",
    noWarranty: "No warranty",
    soldTo: "Sold to {name} on {date}",
    boughtFrom: "Bought from {name} on {date}",
  },
  variants: {
    title: "Sizes & colours: {name}",
    open: "Sizes & colours ({n})",
    variant: "Variant",
    unnamed: "Standard",
    archived: "Not sold",
    archive: "Stop selling this variant",
    add: "Add variants",
    addHint:
      "List the values with commas. Every combination not already here is added, each with its own stock and barcode.",
    attribute: "Attribute",
    values: "Values",
    addAttribute: "Add attribute",
    price: "Price for the new ones",
    priceHint: "New variants start at {price}; change any of them above afterwards.",
    nothingNew: "Nothing new to add",
    create: "Add {n} variants",
    done: "Done",
  },
  supportVisit: {
    bannerRead: "Zeeta support view · look only · ends {time}",
    bannerWrite: "Zeeta support view · changes are recorded as support · ends {time}",
    leave: "Leave",
    title: "Zeeta support visits",
    subtitle:
      "When Zeetabyte's support team opened this business to help, and why. They are emailed to you as they start.",
    none: "Support has never opened this business.",
    read: "Look only",
    write: "Could make changes",
    now: "Now",
    end: "End now",
    endedByYou: "ended by you",
    full: "Full access",
    bannerFull:
      "Zeeta support view · full access as owner · changes are recorded as support · ends {time}",
  },
  biz: {
    listHint: "Businesses: {n}",
    searchHint: "Name, address, owner email or phone",
    status: "Status",
    plan: "Plan",
    type: "Business type",
    anyStatus: "Any status",
    anyPlan: "Any plan",
    anyType: "Any type",
    statuses: {
      active: "Active",
      suspended: "Suspended",
      provisioning: "Setting up",
      closed: "Closed",
    },
    paymentFailed: "Payment failed",
    since: "since {date}",
    until: "until {date}",
    lastActive: "Last active",
    never: "Never",
    pageOf: "Page {page} of {pages}",
    previous: "Previous page",
    next: "Next page",
    tabs: {
      summary: "Summary",
      plan: "Plan & limits",
      members: "Members",
      billing: "Billing",
      sms: "SMS",
      website: "Website",
      integrations: "Integrations",
      support: "Support",
      audit: "Audit",
      danger: "Danger zone",
    },
    sales30d: "Sales, 30 days",
    allTimeSales: "All sales",
    invoices: "{n} invoices",
    lastSale: "Last sale {date}",
    limit: "Limit",
    used: "Used",
    allowed: "Allowed",
    limits: { branches: "Branches", users: "People", items: "Products" },
    counts:
      "{customers} customers · {suppliers} suppliers · {orders} online orders · {storage} of data",
    currentPlan: "{plan} plan",
    setPlanHint: "Moves this business to {plan}. Their limits and features change at once.",
    overrideTitle: "Extra limits and features for this business",
    overrideHint:
      "Leave a box empty to use the plan's limit. Changes here apply only to this business.",
    overrideActive: "Override in force",
    planDefault: "Plan: {value}",
    usePlan: "Plan's limit",
    unlimitedHint: "Type ∞ for no limit.",
    features: {
      vat: "VAT and Mushak",
      offline: "Offline selling",
      multi_branch: "Several branches",
      custom_domain: "Own website address",
      api: "Live API",
      couriers: "Couriers",
      advanced_reports: "Advanced reports",
    },
    on: "On",
    asPlan: "As the plan",
    forceOn: "Always on",
    forceOff: "Always off",
    overrideNote: "Why (shown to staff)",
    overrideNoteHint: "For example: a three-month trial of branches.",
    overrideExpires: "Ends on",
    saveOverride: "Save extra limits",
    saveOverrideHint: "This business will get these limits and features straight away.",
    removeOverride: "Remove extra limits",
    memberEmail: "Gmail address",
    memberEmailHint:
      "Someone with a Zeeta account is added straight away; anyone else is invited by email.",
    addMember: "Add member",
    addMemberHint: "{email} joins as {role}.",
    changeRole: "Change role",
    changeRoleHint: "{name} becomes {role}.",
    removeMember: "Remove member",
    removeMemberHint: "{name} loses access to this business at once.",
    revokeInvite: "Cancel invitation",
    transfer: "Transfer ownership",
    transferHint:
      "{name} becomes the owner. The current owner becomes a manager. Both are emailed.",
    makeOwner: "Make owner",
    remove: "Remove",
    memberSince: "Member since",
    accountDisabled: "Account disabled",
    invitations: "Waiting invitations",
    inviteExpires: "Expires {date}",
    formerMembers: "Former members",
    typeToConfirm: "Type {text} to confirm",
    totalPaid: "Paid in total",
    payments: "Payments",
    forWhat: "For",
    smsPack: "{n} SMS",
    noPayments: "No payments yet.",
    smsBalance: "SMS credits",
    smsMessages: "Recent messages",
    smsLedger: "Credit history",
    grantHint: "Adds {n} credits (a minus number takes them away).",
    websiteOpen: "Open",
    websiteClosed: "Closed",
    visitWebsite: "Visit the website",
    takeOffline: "Take website offline",
    putOnline: "Put website online",
    noWebsite: "This business has not set up a website.",
    domain: "Domain",
    checked: "Last checked",
    primary: "Primary",
    recheck: "Check again",
    removeDomain: "Remove domain",
    couriers: "Couriers",
    courierSecret: "Courier passwords and keys are never shown, not even to staff.",
    apiKeys: "API keys",
    lastUsed: "last used {date}",
    revoked: "Revoked",
    revoke: "Revoke",
    revokeKey: "Revoke API key",
    webhooks: "Webhooks",
    disable: "Turn off",
    enable: "Turn on",
    none: "None",
    staffActions: "What Zeetabyte staff did",
    businessLog: "The business's own log",
    auditSearch: "Search actions",
    action: "Action",
    suspendHint: "Nobody can use the business until it is reactivated. Its data is kept.",
    reactivateHint: "The business can be used again straight away.",
  },
  usr: {
    limitsTitle: "Business limits",
    limitsUsage: "{owned} of {max} businesses · {free} of {maxFree} on the Free plan",
    limitsDefault: "Using the platform limits.",
    limitsCustom: "Custom limits for this person.",
    limitsChange: "Change limits",
    limitsHint:
      "For chains and agencies that need more businesses. Leave a box empty to use the platform limit.",
    limitsBlank: "Empty: platform limit",
    listHint: "Accounts: {n}",
    searchHint: "Email, phone, name or user ID",
    everyone: "Everyone",
    active: "Active",
    disabled: "Disabled",
    staffOnly: "Zeetabyte staff",
    businesses: "Businesses",
    joined: "joined {date}",
    edit: "Change email, phone or name",
    editHint:
      "Only change someone's email after confirming it is really theirs; they sign in with it.",
    signOut: "Sign out everywhere",
    signOutHint: "Ends every session on every device. They can sign in again straight away.",
    reset: "Reset sign-in",
    resetHint:
      "Removes their password, signs them out everywhere and emails them to sign in with a code.",
    disable: "Disable account",
    disableHint:
      "They are signed out everywhere and cannot sign in until enabled again. Their businesses keep running.",
    enable: "Enable account",
    signInTitle: "How they sign in",
    password: "password",
    emailCode: "email code",
    passwordChanged: "Password last changed {date}",
    mustChange: "Must choose a new password at next sign-in",
    removed: "removed",
    devices: "Signed-in devices",
    noDevices: "Not signed in anywhere.",
  },
  bill: {
    hint: "Every payment on the platform. Open one to accept money confirmed by hand, refund or correct its invoice.",
    collected: "Collected",
    refunded: "Refunded",
    net: "Net",
    searchHint: "Business, invoice, our ID or gateway reference",
    kind: "For",
    anyKind: "Plans and SMS",
    kinds: { subscription: "Plan", sms: "SMS credits" },
    gateway: "Paid through",
    anyGateway: "Any way",
    manual: "Recorded by staff",
    statuses: { paid: "Paid", initiated: "Waiting", failed: "Failed", cancelled: "Cancelled" },
    business: "Business",
    refundedAmount: "{amount} refunded",
    reference: "Reference",
    referenceHint: "The bKash/Nagad TrxID, bank reference or receipt number.",
    ourId: "Our ID",
    started: "Started",
    paidAt: "Paid",
    payer: "Paid by",
    billedTo: "Billed to",
    billedToHint: "Replaces the business name and address on the invoice.",
    note: "Note on the invoice",
    refunds: "Refunds",
    markPaid: "Mark as paid",
    markPaidHint:
      "Use this only after checking the money arrived. The plan or credits start straight away and a receipt is emailed.",
    markFailed: "Mark as failed",
    refund: "Refund",
    refundHint: "Record money you have already returned. At most {max}.",
    recordRefund: "Record refund",
    correctInvoice: "Correct invoice",
    correctInvoiceHint: "Amounts never change; refund and record a new payment to fix one.",
    showRaw: "Show the gateway's raw answer",
    hideRaw: "Hide the gateway's raw answer",
    method: "How it was paid",
    subscription: "Subscription",
    endsCancelled: "cancelled, ends {date}",
    recordPayment: "Record a payment",
    recordHint:
      "For money paid outside Zeeta, such as cash or a bank transfer. What it buys starts straight away.",
    extend: "Extend",
    extendHint: "Adds days to the end of the paid period, e.g. to make up for downtime.",
    extraDays: "Extra days",
    cancelAtEnd: "Cancel at period end",
    cancelAtEndHint:
      "The plan keeps working until its paid period ends, then moves to Free with no grace period.",
    resume: "Undo cancellation",
    cancelNow: "Cancel now",
    cancelNowHint: "Moves the business to the Free plan straight away.",
    period: "Period",
    credits: "SMS credits",
  },
  platform: {
    maintenance:
      "Zeeta is read-only for a short while for maintenance. You can look at everything, but changes will wait.",
    dismiss: "Dismiss",
  },
  cfg: {
    history: "Change history",
    showChange: "Show the change",
    noHistory: "No changes yet. Everything is on its default.",
    changed: "Changed from default",
    resetDefault: "Reset to default",
    nameEn: "Name (English)",
    nameBn: "Name (Bangla)",
    descEn: "Description (English)",
    descBn: "Description (Bangla)",
    planContents: "What each plan includes",
    planContentsHint:
      "Limits, monthly SMS and features. Changes reach every business on the plan within a minute; per-business overrides still win.",
    smsPerMonth: "SMS per month",
    selfServe: "Can be bought online",
    resetPlan: "Reset {plan} to default",
    savePlan: "Save {plan}",
    planSaveHint:
      "Every business on this plan gets the new limits and features within a minute. A business already over a lowered limit keeps its data but can't add more.",
    secCodes: "Sign-in codes",
    secPasswords: "Passwords",
    secSessions: "Sessions",
    secAdmin: "Admin console",
    sec: {
      otpTtlMinutes: "Code expires after (minutes)",
      otpMaxAttempts: "Wrong tries per code",
      otpResendSeconds: "Wait before resending (seconds)",
      otpMaxPer15Min: "Codes per 15 minutes",
      otpMaxPerDay: "Codes per day",
      passwordMaxFailures: "Wrong passwords before a pause",
      passwordWindowMinutes: "Pause length (minutes)",
      sessionIdleHours: "Sign out when idle (hours)",
      sessionTrustedIdleDays: "Trusted device idle limit (days)",
      sessionAbsoluteDays: "Sign in again after (days)",
      adminIdleMinutes: "Admin lock when idle (minutes)",
      adminMaxHours: "Admin code lasts (hours)",
    },
    signupTitle: "Who can join",
    signup: {
      allowNewAccounts: "New accounts",
      allowNewBusinesses: "New businesses",
      allowPasswordSignIn: "Password sign-in",
      allowGoogleSignIn: "Google sign-in",
      maxBusinessesPerUser: "Businesses per person",
      maxFreeBusinessesPerUser: "Free-plan businesses per person",
    },
    signupHint: {
      allowNewAccounts: "When off, only people with a pending invitation can create an account.",
      allowNewBusinesses: "When off, people can't create a new business. Staff still can.",
      allowPasswordSignIn: "When off, everyone signs in with an emailed or texted code.",
      allowGoogleSignIn: "When off, the Google button is hidden.",
    },
    limitsTitle: "Limits",
    range: "Between {min} and {max}. Default {def}.",
    outOfRange: "Outside the safe range",
    businessLimitsHint:
      "Counts businesses a person owns or opened, not closed ones. Zeeta staff are not limited; raise one person's limits on their user page.",
    securityHint:
      "Every value is kept inside a safe range so nobody can be locked out. Network rate limits are fixed in Cloudflare and can't be changed here.",
    securitySaveHint:
      "New limits apply to the next sign-in or code request. Existing sessions keep their current expiry until they next refresh.",
    maintenanceHint:
      "Make the app read-only while you fix something. People can still look at everything; saving is paused with your message. Support visits are not affected.",
    everyBusiness: "Every business",
    readOnlyNow: "Read-only now",
    workingNormally: "Working normally",
    endMaintenance: "End maintenance",
    startMaintenance: "Start maintenance",
    messageEn: "Message (English)",
    messageBn: "Message (Bangla)",
    messageHint: "Shown in a banner and when someone tries to save.",
    saveMessage: "Save message",
    someBusinesses: "Single businesses",
    someBusinessesHint:
      "{n} business(es) are read-only on their own. Change this from a business's Danger tab.",
    startHint:
      "Every business becomes read-only at once: sales, stock changes and online orders stop until you end maintenance.",
    readOnlyBusiness: "Make read-only",
    readOnlyBusinessHint:
      "Pause saving for this business only, with the maintenance message. Viewing still works, and support visits are not affected.",
    endReadOnly: "End read-only",
    announceHint:
      "Banners shown inside the app to everyone or to chosen plans, business types or businesses.",
    newAnnouncement: "New announcement",
    severity: { info: "Info", warning: "Warning", critical: "Critical" },
    live: "Live",
    scheduled: "Scheduled",
    ended: "Ended",
    endNow: "End now",
    audience: {
      all: "Everyone",
      plans: "Plans",
      packs: "Business types",
      tenants: "Chosen businesses",
    },
    noAnnouncements: "No announcements yet.",
    titleEn: "Title (English)",
    titleBn: "Title (Bangla)",
    bodyEn: "Text (English)",
    bodyBn: "Text (Bangla)",
    severityLabel: "Severity",
    audienceLabel: "Who sees it",
    businessIds: "Business IDs",
    businessIdsHint: "Separate with commas or spaces. The ID is in the business page address.",
    starts: "Starts",
    ends: "Ends",
    dismissible: "People can hide it",
    typesHint:
      "What each business type offers at sign-up and gives a new business. Changes apply to new businesses; use “Add to existing businesses” to push new categories, units and fields to businesses that already exist.",
    hidden: "hidden",
    offered: "Offered when creating a business",
    modules: "Turned on by default",
    module: {
      variants: "Sizes and colours",
      batches: "Batches",
      expiry: "Expiry dates",
      serials: "Serial numbers",
      vat: "VAT",
      multi_branch: "Branches",
      storefront: "Online shop",
      loans: "Loans",
    },
    categories: "Starter categories",
    pairHint: "One per line: English | বাংলা",
    units: "Units",
    unitsHint: "One per line: code | English | বাংলা | yes/no (fractions allowed)",
    conversions: "Unit conversions",
    conversionsHint: "One per line: from > to = number, e.g. kg > g = 1000",
    priceTiers: "Starter price lists",
    presets: "Size and colour presets",
    presetsHint: "One per line: English | বাংলা: value, value",
    warranty: "Default warranty (months)",
    nearExpiry: "Near-expiry warning (days)",
    customFields: "Extra fields",
    fieldKey: "Field key",
    fieldFor: "Used on",
    fieldType: "Field type",
    entity: { item: "Products", customer: "Customers", supplier: "Suppliers" },
    type: { text: "Text", number: "Number", date: "Date", select: "List", boolean: "Yes/no" },
    required: "Required",
    searchable: "Searchable",
    options: "List options",
    addField: "Add field",
    applyExisting: "Add to existing businesses",
    applyHint:
      "Adds missing categories, units, conversions and extra fields from {name} to every business of this type. Nothing is changed or removed.",
    saveType: "Save {name}",
    saveTypeHint:
      "New businesses of this type start with these settings. Existing businesses are not changed.",
  },
  life: {
    export: "Export all data",
    exportHint:
      "A zip with every table as a CSV file, kept for 7 days. You'll get an email when it's ready.",
    exportConfirm:
      "Every table of this business is written to a zip file. Downloads are recorded in the audit log.",
    close: "Close business",
    closeHint:
      "Members lose access, API keys are revoked, webhooks and the online shop stop. Data is kept for 30 days, then deleted for good.",
    closeConfirm:
      "{name} closes at once and the owners are emailed. It can be restored for 30 days; after that all its data is deleted.",
    closedTitle: "This business is closed",
    closedHint: "Closed on {closed}. Its data will be deleted on {purge} unless it is restored.",
    restore: "Restore business",
    restoreConfirm:
      "The business opens again with all its data, online shop and webhooks as they were. API keys stay revoked.",
    purged: "This business's data has been deleted",
    purgedHint: "Deleted on {date}. Billing history and the audit log are kept.",
  },
  dataExport: {
    title: "Your data",
    hint: "Download everything your business has stored in Zeeta as a zip of spreadsheet (CSV) files. Product images are not included.",
    open: "Download all data",
    prepare: "Prepare a download",
    preparing: "Preparing…",
    none: "No downloads yet.",
    noPermission: "You don't have permission to download the business's data.",
    status: { queued: "Preparing", ready: "Ready", failed: "Failed" },
    expired: "Expired",
    byStaff: "by Zeeta support",
    tables: "{n} tables",
    until: "until {date}",
    download: "Download",
  },
  dash: {
    period: "Period",
    days: "{n} days",
    netRevenue: "Net revenue, {n} days",
    signups: "New businesses, {n} days",
    vsPrevious: "{pct} vs previous period",
    newThisPeriod: "None in the period before",
    noChange: "No change",
    ofBusinesses: "of {n} active businesses",
    activeToday: "Businesses active today",
    statusSplit: "{suspended} suspended · {closed} closed",
    revenueByDay: "Revenue by day",
    signupsByDay: "New businesses by day",
    revenueByPlan: "Revenue by plan",
    smsPacks: "SMS packs",
    payments: "{n} payments",
    noRevenue: "No payments in this period.",
    refundsNote: "{gross} collected, {refunds} refunded. The chart shows money collected.",
    needsAttention: "Needs attention",
    failedPayments: "Failed payments, {n} days",
    errors24h: "Open errors, last 24 hours",
    openVisits: "Support visits open now",
    nearLimits: "Businesses near a plan limit",
    nearLimitsHint:
      "At 80% or more of a limit. Usage is refreshed nightly and whenever you open a business.",
    smsLine: "{n} SMS ({segments} segments) in the period, {failed} failed.",
  },
  logs: {
    staff: "Staff",
    staffPlaceholder: "Staff email",
    action: "Action",
    allActions: "All actions",
    businessId: "Business ID",
    business: "Business",
    from: "From",
    to: "To",
    csv: "Download CSV",
    system: "System",
    empty: "Nothing matches these filters.",
    loadMore: "Load more",
    errors: {
      open: "Open",
      resolved: "Resolved",
      noneResolved: "No resolved errors in the last 7 days.",
      reopened: "Happened again",
      resolve: "Mark resolved",
      reopen: "Reopen",
      note: "What fixed it (optional)",
      resolvedBy: "Resolved by {who} on {date}",
    },
  },
  ai: {
    ask: "Ask Zeeta",
    intro:
      "Ask about your sales, stock, dues or expenses, in Bangla or English. Answers come from your own reports.",
    suggest: {
      today: "How were sales today?",
      week: "How much did I sell this week compared with last week?",
      dues: "Who owes me the most?",
      runningOut: "Which products will run out soon?",
      bestSellers: "What were my best sellers this month?",
    },
    question: "Your question",
    placeholder: "Type a question…",
    listening: "Listening…",
    speak: "Speak your question",
    stopListening: "Stop listening",
    send: "Send",
    thinking: "Looking at your reports…",
    remaining: "{n} of {quota} AI requests left this month.",
    privacy: "Customer and supplier names are never sent to the AI provider.",
    notInPlan: "Ask Zeeta isn't included in your plan. Upgrade from the Billing page to use it.",
    unavailable: "Ask Zeeta isn't available right now. Please try again later.",
  },
  aiAdmin: {
    hint: "The AI features use OpenAI's GPT-6 Luna through Cloudflare AI, billed to the Cloudflare account. Shop figures and product names are sent to answer questions; customer, supplier and staff names, phone numbers and addresses are not.",
    connected: "Connected · {model}",
    notConfigured: "Not set up",
    setupHint:
      'This environment has no Cloudflare AI binding ("ai": { "binding": "AI" } in wrangler.jsonc). Until it does, the AI features stay hidden.',
    enabled: "AI features on for every business",
    weekly: "Email owners a weekly summary",
    quota: "AI requests per month, by plan",
    quotaHint: "0 turns AI off for that plan. Weekly summaries don't count toward the allowance.",
    save: "Save AI settings",
    requests30: "Requests, 30 days",
    cost30: "Cost, 30 days",
    tokens: "Tokens",
    errors: "Failed requests",
    byFeature: "By feature",
    byBusiness: "Top businesses by cost",
    requests: "Requests",
    cost: "Cost",
    feature: {
      ask: "Ask Zeeta",
      item: "Product helper",
      invoice: "Invoice reader",
      summary: "Weekly summary",
    },
  },
  aiItem: {
    suggest: "Suggest details",
    check: "Suggested by AI. Check the names, category and unit before saving.",
  },
  aiInvoice: {
    scan: "Scan invoice",
    reading: "Reading invoice…",
    photo: "Invoice photo",
    unreadable:
      "The photo couldn't be read as an invoice. Try a clearer, flatter photo in good light.",
    filled: "Filled {n} of {total} lines from the invoice.",
    supplierNotFound: 'Supplier "{name}" isn\'t in your list. Choose one or add it.',
    notMatched:
      "These lines didn't match any of your products. Add them with the search box, or create the products first:",
    totalDiffers:
      "The invoice total reads {read}, but these lines come to {now}. Check quantities and prices.",
    check: "Read by AI. Check every line against the paper before saving.",
  },
  aiDigest: {
    title: "This week's summary",
    week: "{from} – {to}",
    setting: "Email me a weekly summary",
    settingHint:
      "Every Saturday morning: how sales went, best sellers, what to reorder and who owes you. Sent to owners.",
  },
  printer: {
    title: "Receipt printer",
    hint: "Pair the printer in this phone's Bluetooth settings first, then find it here. Each phone keeps its own printer.",
    settingsHint:
      "In the Zeeta Android app, receipts can print straight to a Bluetooth thermal printer, with Bangla, and open the cash drawer.",
    open: "Set up the receipt printer",
    browserOnly: "Direct Bluetooth printing works in the Zeeta Inventory Management Android app.",
    browserHint:
      "In a browser, receipts print through the print screen: choose your printer there, with the paper size set to 58 mm or 80 mm.",
    find: "Find paired printers",
    pair: "Pair a new printer",
    noneFound:
      "No paired devices. Pair the printer in Bluetooth settings (the PIN is often 0000 or 1234), then try again.",
    choose: "Choose the printer",
    current: "Printer",
    paper: "Paper width",
    autoPrint: "Print the receipt as soon as a sale is completed",
    drawer: "Open the cash drawer for cash sales",
    test: "Print a test",
    testLine: "Test print",
    testOk: "If you can read this, the printer is ready.",
    testSent: "Test sent to the printer.",
    saved: "Printer saved on this phone.",
    forget: "Forget printer",
    failed: "The printer did not answer. Check that it is on, nearby and paired.",
  },
  labels: {
    rollTitle: "Thermal sticker roll",
    rollHint:
      "Choose the sticker size printed on your roll. The layout fits the size: small stickers keep the name, price and barcode; larger ones also show the shop name and more.",
    size: "Sticker size",
    columns: "Stickers across",
    columnGap: "Gap across (mm)",
    tooWide: "That is {mm} mm across, wider than label printers print. Use fewer stickers across.",
    previewHint: "Shown at its real size: exactly what the printer receives.",
    printComputer: "Print {n} stickers",
    printBluetooth: "Print over Bluetooth ({name})",
    computerHint:
      "On a computer, choose your label printer in the print dialog, with the paper size set to the sticker size (most label printer drivers have it), scale 100% and no margins.",
    rollInUse: "Roll in use: {size}, {n} across. Change it on the Print labels page.",
    title: "Print labels",
    hint: "Barcode stickers for your products. A product without a barcode gets the shop's own (starting 20), which scans at the till straight away.",
    add: "Add a product to print…",
    empty: "Add the products you want stickers for.",
    barcode: "Barcode",
    copies: "Stickers",
    newBarcode: "New barcode",
    nameIn: "Product name in",
    showShop: "Shop name",
    showPrice: "Price",
    showMrp: "MRP",
    mrp: "MRP",
    preview: "Preview",
    sheetTitle: "A4 sticker sheet",
    sheetHint:
      "Any printer. Buy A4 label sheets of the size below, set the print scale to 100% (no fit to page) and margins to none.",
    sheet: "Sheet",
    sheetOption: "{n} per sheet ({size} mm)",
    skip: "Already used",
    skipHint: "Stickers to skip on a partly used sheet.",
    printSheet: "Print {n} stickers ({pages} pages)",
    printerTitle: "Label printer",
    printerHint:
      "A Bluetooth label printer (most use TSPL), or a receipt printer loaded with sticker paper. Set the sticker size printed on the roll.",
    printerNone: "No label printer chosen on this phone yet.",
    printingProgress: "Printing {done} of {total}…",
    back: "Back to products",
    print: "Print labels",
    forPurchase: "Labels for this purchase",
    printerKind: "Printer type",
    kindTspl: "Label printer (TSPL)",
    kindEscpos: "Receipt printer with sticker paper",
    width: "Sticker width (mm)",
    height: "Sticker height (mm)",
    gap: "Gap (mm)",
  },
  scan: {
    notFound: "No product has the barcode {code}",
    camera: "Scan with camera",
    cameraHint:
      "Hold each barcode inside the frame; it is added as soon as it is read. Keep scanning, then tap Done.",
    cameraDenied:
      "Camera permission was not given. Allow the camera for this site in the browser or app settings.",
    cameraFailed: "The camera could not be started on this device.",
    torch: "Light",
    cameraTips:
      "Not reading yet? Hold the barcode flat across the red line, about 15 cm (a hand's width) away, in good light. Tap Light if it's dark. Or close this and type the number under the bars.",
    added: "Added {name} · {n} scanned",
    done: "Done",
    multiplyHint:
      "Scan items with the scanner or the camera button. For many of the same item, type the number and * (like 12*) and then scan.",
  },
  adminPin: {
    wrong: "Wrong PIN. Tries left: {n}.",
    title: "Unlock PIN",
    hint: "A 6-digit PIN unlocks the admin when it has locked itself, without waiting for an email. It works on a browser for 7 days after you confirm an emailed code there; a new browser always needs the code first.",
    notSet: "No PIN yet: every unlock needs an emailed code.",
    isSet: "PIN is set. Changing or removing it emails you a notice.",
    pin: "PIN (6 digits)",
    newPin: "New PIN (6 digits)",
    again: "Same PIN again",
    mismatch: "The two PINs are different",
    set: "Set PIN",
    change: "Change PIN",
    remove: "Remove PIN",
    saved: "PIN saved.",
    removed: "PIN removed.",
    unlockTitle: "Admin locked",
    unlockHint: "Enter your PIN to carry on as {email}.",
    unlock: "Unlock",
    useEmail: "Use an emailed code instead",
    usePin: "Unlock with my PIN instead",
    tooManyTries: "Too many wrong PINs. Confirm an emailed code and your PIN works again.",
    emailDue:
      "Your PIN works on this browser again once you confirm an emailed code (needed every 7 days).",
    setLater: "Tip: set an unlock PIN on your admin account page to skip this email next time.",
  },
  barcodeLib: {
    hint: "Factory barcodes that shops have added: name, brand and size only, never prices or which shop. Approved ones fill in the product form for every shop.",
    status: "Status",
    pending: "Waiting",
    approved: "Approved",
    rejected: "Rejected",
    search: "Barcode, name or brand",
    empty: "Nothing here.",
    shops: "Added by {n} shop(s)",
    size: "Pack size",
    approve: "Approve",
    update: "Save changes",
    reject: "Reject",
  },
  businessLimit: {
    title: "You're at your limit",
    paused: "New businesses are paused for now. Please try again later.",
    max: "You can have at most {max} businesses.",
    maxHelp: "Close one you no longer use, or contact Zeeta support if you run a larger chain.",
    free: "You can have {count} business on the Free plan.",
    noFree: "New businesses need a paid plan.",
    freeHelp: "Move your Free business to a paid plan, and you can open another one.",
    upgrade: "Upgrade {name}",
  },
  scanTest: {
    title: "Test your barcode scanner",
    hint: "Click the box and scan any product. You'll see what the scanner sends.",
    placeholder: "Scan here",
    code: "Code",
    kind: "Type",
    factory: "Maker's barcode (can be looked up)",
    shopCode: "Shop's own barcode",
    other: "Other code or SKU",
    speed: "Speed",
    ms: "{ms} ms between characters",
    ending: "Ends with",
    end: { enter: "Enter", tab: "Tab", none: "Nothing" },
    raw: "As typed",
    ok: "This scanner works with Zeeta.",
    tooSlow:
      "This scanner types slowly. Scans still work when the cursor is in the barcode box. To scan from anywhere, set it to fast or high-speed USB mode (see its manual).",
    bangla:
      "A Bangla keyboard is on. Zeeta reads the digits correctly, but switching to English while scanning is safest.",
  },
  theme: {
    label: "Appearance",
    light: "Light",
    dark: "Dark",
    system: "Device setting",
  },
  roles: {
    owner: "Owner",
    manager: "Manager",
    cashier: "Cashier",
    stock_keeper: "Stock keeper",
    accountant: "Accountant",
    support: "Zeeta support",
  },
};

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };
export type Messages = Shape<typeof en>;
